// ============================================================================
// Mutations TanStack Query — ACCUEIL E1b : publier / reagir / commenter
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). TOUTES les mutations sont
// OPTIMISTES avec ROLLBACK sur erreur (MASTER-GEST-E1B) : snapshot des caches
// touches dans onMutate (apres cancelQueries), restauration dans onError,
// invalidation de reconciliation dans onSettled (le serveur reste l'autorite —
// id reels, compteurs projetes, myReaction). L'AUTORISATION vit au SERVEUR
// (bind-to-fetch) : ici on ne fait qu'anticiper le verdict attendu ; un refus
// serveur (403/404) declenche le rollback + un code court mappe i18n par
// l'appelant (accueilErrorCode — jamais de code brut a l'ecran, GA-21).
//
// Piege UQ mig 144 anticipe : UNE reaction par user par publication (tous
// types confondus) — poser un autre type REMPLACE la reaction courante
// (l'optimiste decremente l'ancien compteur, incremente le nouveau).

import { useMutation, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { api } from '@gs/api-client';
import {
  ACCUEIL_FEED_KEY, accueilCommentsKey,
  type Publication, type PublicationsPage, type PublicationComment,
  type PublicationCommentsResponse, type PublicationCategory, type ReactionType,
} from './useAccueil';

type FeedData = InfiniteData<PublicationsPage>;
type FeedSnapshot = Array<[readonly unknown[], FeedData | undefined]>;

/** Identite du viewer pour le rendu optimiste (affichage seulement — le serveur force la session). */
export interface OptimisticAuthor {
  email: string;
  name: string | null;
}

// ─── Helpers caches feed (toutes les variantes categorie en cache) ───────────

async function snapshotFeeds(qc: QueryClient): Promise<FeedSnapshot> {
  await qc.cancelQueries({ queryKey: ACCUEIL_FEED_KEY });
  return qc.getQueriesData<FeedData>({ queryKey: ACCUEIL_FEED_KEY });
}

function restoreFeeds(qc: QueryClient, snapshot: FeedSnapshot | undefined): void {
  for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data);
}

function mapFeedItems(qc: QueryClient, update: (p: Publication) => Publication): void {
  qc.setQueriesData<FeedData>({ queryKey: ACCUEIL_FEED_KEY }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((pg) => ({ ...pg, publications: pg.publications.map(update) })),
        }
      : data,
  );
}

// ─── Publier ──────────────────────────────────────────────────────────────────

export interface PublishInput {
  title: string;
  body: string;
  category: PublicationCategory;
  visibility: 'public' | 'management';
}

/**
 * Publie une annonce — prepend optimiste (id temporaire) dans chaque variante
 * de fil en cache dont le filtre couvre la categorie ; rollback sur erreur ;
 * reconciliation serveur (vrai id, posted_at serveur) a l'issue.
 */
export function usePublishPublication(author: OptimisticAuthor) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PublishInput) => api.post<{ id: string }>('/api/publications', input),
    onMutate: async (input) => {
      const snapshot = await snapshotFeeds(qc);
      const temp: Publication = {
        id: `optimistic-${Date.now()}`,
        title: input.title,
        body: input.body,
        category: input.category,
        authorEmail: author.email,
        authorName: author.name,
        postedAt: new Date().toISOString(),
        reactions: { like: 0, love: 0, celebrate: 0 },
        viewCount: 0,
        commentCount: 0,
        myReaction: null,
      };
      // Cle = [...FEED_KEY, { category }] — ne prepend que dans les variantes
      // dont le filtre couvre la publication (le snapshot vient d'etre pris).
      for (const [key, data] of snapshot) {
        if (!data || data.pages.length === 0) continue;
        const filter = (key[key.length - 1] as { category: PublicationCategory | null } | undefined)
          ?.category;
        if (filter && filter !== input.category) continue;
        const [first, ...rest] = data.pages;
        qc.setQueryData<FeedData>(key, {
          ...data,
          pages: [{ ...first, publications: [temp, ...first.publications] }, ...rest],
        });
      }
      return { snapshot };
    },
    onError: (_err, _input, ctx) => restoreFeeds(qc, ctx?.snapshot),
    onSettled: () => void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY }),
  });
}

// ─── Reagir (toggle — une reaction par user par publication) ─────────────────

export interface ToggleReactionInput {
  publicationId: string;
  type: ReactionType;
  /** Reaction ACTIVE courante du viewer (myReaction du fil — verdict serveur). */
  current: ReactionType | null;
}

/**
 * Toggle de reaction : meme type => retrait (DELETE) ; autre type => pose/
 * remplacement (PUT — l'optimiste bascule les compteurs ancien/nouveau).
 */
export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicationId, type, current }: ToggleReactionInput) =>
      current === type
        ? api.delete<{ ok: boolean }>(`/api/publications/${publicationId}/reaction`)
        : api.put<{ ok: boolean }>(`/api/publications/${publicationId}/reaction`, { type }),
    onMutate: async ({ publicationId, type, current }) => {
      const snapshot = await snapshotFeeds(qc);
      mapFeedItems(qc, (p) => {
        if (p.id !== publicationId) return p;
        const reactions = { ...p.reactions };
        if (current) reactions[current] = Math.max(0, reactions[current] - 1);
        const removing = current === type;
        if (!removing) reactions[type] += 1;
        return { ...p, reactions, myReaction: removing ? null : type };
      });
      return { snapshot };
    },
    onError: (_err, _input, ctx) => restoreFeeds(qc, ctx?.snapshot),
    onSettled: () => void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY }),
  });
}

// ─── Commenter ────────────────────────────────────────────────────────────────

/**
 * Ajoute un commentaire — append optimiste dans le fil de discussion +
 * commentCount du fil ; le serveur retourne le commentaire reel (remplace
 * l'entree temporaire au success, rollback complet sur erreur).
 */
export function useAddComment(publicationId: string, author: OptimisticAuthor) {
  const qc = useQueryClient();
  const commentsKey = accueilCommentsKey(publicationId);
  return useMutation({
    mutationFn: (body: string) =>
      api.post<{ comment: PublicationComment }>(`/api/publications/${publicationId}/comments`, { body }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: commentsKey });
      const feedSnapshot = await snapshotFeeds(qc);
      const commentsSnapshot = qc.getQueryData<PublicationCommentsResponse>(commentsKey);
      const tempId = `optimistic-${Date.now()}`;
      qc.setQueryData<PublicationCommentsResponse>(commentsKey, (data) => ({
        comments: [
          ...(data?.comments ?? []),
          {
            id: tempId,
            authorEmail: author.email,
            authorName: author.name,
            body,
            createdAt: new Date().toISOString(),
            canDelete: true,
          },
        ],
      }));
      mapFeedItems(qc, (p) => (p.id === publicationId ? { ...p, commentCount: p.commentCount + 1 } : p));
      return { feedSnapshot, commentsSnapshot, tempId };
    },
    onSuccess: (res, _body, ctx) => {
      // Reconciliation ciblee : l'entree temporaire prend l'identite serveur.
      qc.setQueryData<PublicationCommentsResponse>(commentsKey, (data) =>
        data
          ? { comments: data.comments.map((c) => (c.id === ctx?.tempId ? res.comment : c)) }
          : data,
      );
    },
    onError: (_err, _body, ctx) => {
      qc.setQueryData(commentsKey, ctx?.commentsSnapshot);
      restoreFeeds(qc, ctx?.feedSnapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY });
    },
  });
}

/** Supprime un commentaire (affordance canDelete — verdict serveur) : retrait optimiste + rollback. */
export function useDeleteComment(publicationId: string) {
  const qc = useQueryClient();
  const commentsKey = accueilCommentsKey(publicationId);
  return useMutation({
    mutationFn: (commentId: string) =>
      api.delete<{ ok: boolean }>(`/api/publications/${publicationId}/comments/${commentId}`),
    onMutate: async (commentId) => {
      await qc.cancelQueries({ queryKey: commentsKey });
      const feedSnapshot = await snapshotFeeds(qc);
      const commentsSnapshot = qc.getQueryData<PublicationCommentsResponse>(commentsKey);
      qc.setQueryData<PublicationCommentsResponse>(commentsKey, (data) =>
        data ? { comments: data.comments.filter((c) => c.id !== commentId) } : data,
      );
      mapFeedItems(qc, (p) =>
        p.id === publicationId ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p,
      );
      return { feedSnapshot, commentsSnapshot };
    },
    onError: (_err, _commentId, ctx) => {
      qc.setQueryData(commentsKey, ctx?.commentsSnapshot);
      restoreFeeds(qc, ctx?.feedSnapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY });
    },
  });
}
